import React, { useCallback, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useToast } from "../../common/hooks/useToast";
import { Popover } from "../../common/popover/Popover";
import { Item } from "../../styles/common";
import { OtpCounterTable } from "./OtpCounterTable";
import { RawCounterReset } from "./RawCounterReset";
import { OTP_BLOCK_STATUS } from "./rateLimits.queries";
import { UNBLOCK_OTP_RECIPIENT } from "./rateLimits.mutation";
import {
  OtpBlockStatus,
  UnblockOtpResult,
  describeCorrelation,
  describeError,
  firstBlockingCounter,
  formatCountdown,
  formatRetryAfter,
  getRateLimitRejection,
  pluralize,
  summarizeUnblock,
} from "./rateLimit";

type RowState = "pending" | "done" | "error";

/**
 * Look up, then unblock — two steps on purpose.
 *
 * The screen this replaces was one input and a Reset button, which invites a
 * guess: an admin pastes whatever the user gave them, gets a success message
 * whether or not it matched anything, and has no way to tell that the block
 * was actually on the IP. Making the lookup a mandatory first step means the
 * unblock is always aimed at something the admin has seen the evidence for.
 *
 * Nothing here refreshes on a timer. Every call on this screen shares the
 * `admin-export` budget (5/min, 30/hour, 100/day per admin) with the unblocks
 * themselves, so a polling panel would throttle an admin out mid-incident.
 * Reads happen on submit and on the explicit refresh button, nowhere else.
 */
export const OtpUnblockPanel: React.FC = (): JSX.Element => {
  const toast = useToast();

  const [identifier, setIdentifier] = useState("");

  /**
   * The exact string that produced the status currently on screen.
   *
   * Kept separate from the live input so that editing the box cannot leave a
   * lookup for one value next to an Unblock button that would fire on
   * another. Also what the mutation sends: the string the admin saw the
   * evidence for, not whatever the field happens to hold when they click.
   */
  const [submitted, setSubmitted] = useState<string | null>(null);

  const [unblockResult, setUnblockResult] = useState<UnblockOtpResult | null>(
    null
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [unblocking, setUnblocking] = useState(false);
  const [rowState, setRowState] = useState<Record<string, RowState>>({});

  const [runLookup, { data, loading, error }] = useLazyQuery(OTP_BLOCK_STATUS, {
    // Without this a repeat lookup — including the refresh button — is served
    // from cache, so the admin sees pre-unblock counters and thinks it failed.
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [unblockRecipient] = useMutation(UNBLOCK_OTP_RECIPIENT);

  const status: OtpBlockStatus | null = data?.otpBlockStatus ?? null;

  const handleLookup = useCallback(
    (value: string) => {
      const target = value.trim();

      if (!target) return;

      /*
       * Re-reading the *same* identifier — the refresh button, or "look up
       * again" after an unblock — keeps the outcome on screen. The skipped
       * destination list in particular is the only record of who is still
       * blocked, it is not re-derivable from a fresh lookup of the IP, and
       * clearing it would silently strand exactly the people the cascade
       * refusal exists to name.
       */
      if (target !== submitted) {
        setUnblockResult(null);
        setRowState({});
      }

      setSubmitted(target);
      runLookup({ variables: { identifier: target } });
    },
    [runLookup, submitted]
  );

  const handleUnblock = useCallback(async () => {
    if (!submitted) return;

    setConfirmOpen(false);
    setUnblocking(true);

    try {
      const result = await unblockRecipient({
        variables: { identifier: submitted },
      });

      const payload: UnblockOtpResult | undefined =
        result.data?.unblockOtpRecipient;

      if (!payload) {
        toast.error("The server returned no result for that unblock.");
        return;
      }

      setUnblockResult(payload);
    } catch (mutationError) {
      // The global Apollo error link announces rate-limit rejections already.
      if (!getRateLimitRejection(mutationError)) {
        toast.error(describeError(mutationError, "Could not unblock that recipient"));
      }
    } finally {
      setUnblocking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted, unblockRecipient]);

  /** One row of `skippedDestinations` — each is a separate admin call. */
  const handleUnblockDestination = useCallback(
    async (destination: string) => {
      setRowState((previous) => ({ ...previous, [destination]: "pending" }));

      try {
        const result = await unblockRecipient({
          variables: { identifier: destination },
        });

        const payload: UnblockOtpResult | undefined =
          result.data?.unblockOtpRecipient;

        if (!payload) {
          setRowState((previous) => ({ ...previous, [destination]: "error" }));
          return;
        }

        setRowState((previous) => ({ ...previous, [destination]: "done" }));
        toast.success(
          `${pluralize(payload.countersCleared, "counter")} cleared for ${destination}.`
        );
      } catch (mutationError) {
        setRowState((previous) => ({ ...previous, [destination]: "error" }));

        if (!getRateLimitRejection(mutationError)) {
          toast.error(
            describeError(mutationError, `Could not unblock ${destination}`)
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unblockRecipient]
  );

  const lookupRejection = getRateLimitRejection(error);
  const blockingCounter = status ? firstBlockingCounter(status.counters) : null;
  const cascadeRefused = status?.cascadeWouldBeRefused ?? false;
  const memberCount = status?.correlation?.destinations ?? 0;

  /** The input has moved on from the result below it. */
  const drifted = submitted !== null && identifier.trim() !== submitted;
  const canUnblock =
    Boolean(status) && !drifted && !unblocking && unblockResult === null;

  return (
    <Item sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Unblock someone
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Look up what is blocking a user, then clear it. Use this when a real
        person is locked out of signup or login.
      </Typography>

      {/* ------------------------------------------------ Step 1 — look up */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
        Step 1 — Look up
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Phone numbers need their country code (+15551234567). Without it the
        lookup matches nothing.
      </Typography>

      <Stack
        direction="row"
        spacing={2}
        alignItems="flex-start"
        flexWrap="wrap"
        rowGap={2}
      >
        <TextField
          label="Identifier"
          placeholder="IP, email, phone, or device id"
          size="small"
          value={identifier}
          disabled={loading}
          onChange={(event) => setIdentifier(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleLookup(identifier);
          }}
          sx={{ minWidth: 340 }}
        />

        <Button
          variant="contained"
          sx={{ mt: 0.5 }}
          disabled={loading || !identifier.trim()}
          onClick={() => handleLookup(identifier)}
        >
          {loading ? "Looking up…" : "Look up"}
        </Button>

        {status && !drifted && (
          <Tooltip title="Re-reads the counters. Costs one of your 5 admin calls per minute.">
            <span>
              <Button
                variant="outlined"
                sx={{ mt: 0.5 }}
                disabled={loading}
                onClick={() => handleLookup(submitted ?? "")}
              >
                Refresh
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      {lookupRejection && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <AlertTitle>The lookup hit its own rate limit</AlertTitle>
          {lookupRejection.message}
          {lookupRejection.retryAfterSeconds !== undefined && (
            <> Try again in {formatRetryAfter(lookupRejection.retryAfterSeconds)}.</>
          )}
        </Alert>
      )}

      {error && !lookupRejection && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {describeError(error, "Could not look that identifier up")}
        </Alert>
      )}

      {submitted && !loading && !error && !status && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>Nothing found for “{submitted}”</AlertTitle>
          The server returned no status for that value. Check the format — phone
          numbers need a country code (+15551234567 or +1 (555) 123-4567), and a
          bare local number matches nothing.
        </Alert>
      )}

      {status && (
        <Box sx={{ mt: 3 }}>
          {drifted && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This result is for <code>{submitted}</code>, not what is in the
              box now. Look up the new value before unblocking.
            </Alert>
          )}

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            flexWrap="wrap"
            rowGap={1}
            sx={{ mb: 1.5 }}
          >
            <Typography variant="body2" color="text.secondary">
              Result for <code>{status.identifier}</code>
            </Typography>

            {/*
             * Rule 4: echo how the server read the string. A phone number that
             * gets read as a device id returns an empty lookup, and without
             * this line an obvious typo looks like a broken screen.
             */}
            <Tooltip title="How the server interpreted what you typed. If this is wrong, the lookup is answering a different question than you asked.">
              <Chip
                size="small"
                variant="outlined"
                label={`Read as: ${status.kind}`}
              />
            </Tooltip>
          </Stack>

          {status.blocked ? (
            <Alert severity="error" icon={false} sx={{ mb: 2 }}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                flexWrap="wrap"
                rowGap={1}
              >
                <Chip size="small" color="error" label="BLOCKED" />
                {blockingCounter ? (
                  <Typography variant="body2">
                    <strong>{blockingCounter.tier}</strong>,{" "}
                    {blockingCounter.used}/{blockingCounter.limit}, resets in{" "}
                    {formatCountdown(blockingCounter.resetsInSeconds)}
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    Blocked by something other than the counters below — most
                    likely the durable registration backstop, which the unblock
                    also clears.
                  </Typography>
                )}
              </Stack>
            </Alert>
          ) : (
            <Alert severity="success" icon={false} sx={{ mb: 2 }}>
              Not currently blocked.
            </Alert>
          )}

          <OtpCounterTable counters={status.counters} />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Countdowns are from the moment of this lookup and do not tick. Hit
            Refresh for current numbers.
          </Typography>

          {status.correlation && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              {describeCorrelation(status.correlation)}
            </Typography>
          )}

          <Divider sx={{ my: 3 }} />

          {/* --------------------------------------- Step 2 — unblock */}
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
            Step 2 — Unblock
          </Typography>

          {/*
           * Rule 3: when the server would refuse to cascade, a button reading
           * "Unblock this recipient" is a lie — it clears the IP's own keys and
           * leaves every member behind it blocked. State the scope first.
           */}
          {cascadeRefused && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>
                This IP has {pluralize(memberCount, "member")} behind it
              </AlertTitle>
              Unblocking will clear the IP&apos;s own limits only; the{" "}
              {memberCount} destinations must be unblocked individually. They
              will be listed here once you do.
            </Alert>
          )}

          {unblockResult ? (
            <UnblockOutcome
              result={unblockResult}
              rowState={rowState}
              onUnblockDestination={handleUnblockDestination}
              onLookupAgain={() => handleLookup(submitted ?? "")}
            />
          ) : (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
              rowGap={1}
            >
              <Button
                variant="contained"
                color={cascadeRefused ? "warning" : "primary"}
                disabled={!canUnblock}
                onClick={() => setConfirmOpen(true)}
              >
                {unblocking
                  ? "Unblocking…"
                  : cascadeRefused
                  ? "Unblock this IP only"
                  : "Unblock this recipient"}
              </Button>

              {drifted && (
                <Typography variant="caption" color="text.secondary">
                  Look up the current value first.
                </Typography>
              )}
            </Stack>
          )}
        </Box>
      )}

      <RawCounterReset />

      {/* ------------------------------------------------ Confirm */}
      <Popover open={confirmOpen} handleClose={() => setConfirmOpen(false)}>
        {/*
         * `Item`, not `Box`: reactjs-popup's own stylesheet is never imported,
         * so the popup wrapper is transparent and the content has to bring its
         * own surface or the dialog renders unreadable over the page beneath.
         */}
        <Item sx={{ p: 3, maxWidth: 520, boxShadow: 6 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {cascadeRefused
              ? "Unblock this IP only?"
              : "Unblock this recipient?"}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            Target: <code>{submitted}</code>
            {status && <> — read as {status.kind}.</>}
          </Typography>

          <Typography variant="body2" sx={{ mb: 2 }}>
            {cascadeRefused ? (
              <>
                This clears the counters held against the IP itself. The{" "}
                {pluralize(memberCount, "destination")} behind it stay blocked
                and will need unblocking one at a time.
              </>
            ) : (
              <>
                This clears every counter held against this recipient and
                everything correlated with it — destinations, IP addresses,
                devices and accounts.
              </>
            )}{" "}
            <strong>It cannot be undone.</strong> Whoever was being slowed down
            by these limits, including anyone abusing them, gets a clean slate.
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color={cascadeRefused ? "warning" : "primary"}
              onClick={handleUnblock}
            >
              {cascadeRefused ? "Unblock IP only" : "Unblock"}
            </Button>
          </Stack>
        </Item>
      </Popover>
    </Item>
  );
};

/* ------------------------------------------------------------------ *
 * Outcome, including the skipped members the refusal exists to report
 * ------------------------------------------------------------------ */

interface OutcomePropsInterface {
  result: UnblockOtpResult;
  rowState: Record<string, RowState>;
  onUnblockDestination: (destination: string) => void;
  onLookupAgain: () => void;
}

const UnblockOutcome: React.FC<OutcomePropsInterface> = ({
  result,
  rowState,
  onUnblockDestination,
  onLookupAgain,
}) => (
  <Box>
    <Alert severity="success">
      <AlertTitle>Unblocked</AlertTitle>
      {/* "counters", never "requests" — this wording ends up in the audit. */}
      {summarizeUnblock(result)}
      {result.registrationBackstopCleared && (
        <> The durable registration backstop was released too.</>
      )}
      {!result.cascaded && result.skippedDestinations.length === 0 && (
        <> This cleared the identifier itself only.</>
      )}
    </Alert>

    {result.skippedDestinations.length > 0 && (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          Still blocked —{" "}
          {pluralize(result.skippedDestinations.length, "destination")} behind
          this IP
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The server would not clear these as part of one call. Unblock the ones
          that belong to real users. Each is its own admin call, and admin
          actions are capped at 5 per minute — work down the list steadily
          rather than clicking every row at once.
        </Typography>

        <List dense disablePadding>
          {result.skippedDestinations.map((destination) => {
            const state = rowState[destination];

            return (
              <ListItem
                key={destination}
                divider
                secondaryAction={
                  state === "done" ? (
                    <Chip size="small" color="success" label="Unblocked" />
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      color={state === "error" ? "error" : "primary"}
                      disabled={state === "pending"}
                      onClick={() => onUnblockDestination(destination)}
                    >
                      {state === "pending"
                        ? "Unblocking…"
                        : state === "error"
                        ? "Retry"
                        : "Unblock"}
                    </Button>
                  )
                }
              >
                <ListItemText
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: { fontFamily: "monospace", overflowWrap: "anywhere" },
                  }}
                  primary={destination}
                />
              </ListItem>
            );
          })}
        </List>
      </Box>
    )}

    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      flexWrap="wrap"
      rowGap={1}
      sx={{ mt: 2 }}
    >
      <Button size="small" onClick={onLookupAgain}>
        Look up again
      </Button>
      <Typography variant="caption" color="text.secondary">
        The counters above are from before this unblock.
      </Typography>
    </Stack>
  </Box>
);
